<template>
  <v-menu
    v-if="hasChildren"
    open-on-hover
    :location="isTop ? topLevelDirection : direction"
    :transition="false"
    :disabled="disabled"
    content-class="dppl-nested-dropdown-content"
    :open-delay="0"
    :close-delay="50"
    @update:model-value="updateModelValue"
  >
    <template #activator="{ props: activatorProps }">
      <component
        :is="href ? 'a' : 'div'"
        class="item p5 h-100 d-flex align-center justify-space-between cursor-pointer"
        :class="{ 'has-divider': hasDivider, disabled, open }"
        v-bind="{ ...activatorProps, ...attributes, class: $attrs.class || '' }"
        :href="href"
        @mouseenter="isTop ? $emit('topMouseEnter') : null"
        @click="disabled ? () => ({}) : onClick()"
      >
        <slot
          v-if="isTop"
          name="topLabel"
        >
          <span>{{ label }}</span>
        </slot>
        <div v-else>
          <component
            :is="prependIcon"
            v-if="prependIcon"
            :class="prependIconClass"
          />
          <span>{{ label }}</span>
          <v-icon class="ml-2" icon="$chevron-right" />
        </div>
      </component>
    </template>
    <DpplNestedDropdownChildren :children="children" />
  </v-menu>
  <component
    :is="href ? 'a' : 'div'"
    v-else
    class="item p5 h-100 d-flex align-center cursor-pointer"
    :class="{ 'has-divider': hasDivider, disabled }"
    v-bind="attributes"
    :href="href"
    @click="disabled ? () => ({}) : onClick()"
  >
    <component
      :is="prependIcon"
      v-if="prependIcon"
      :class="prependIconClass"
    />
    {{ label }}
  </component>
</template>

<script setup>
import { computed, ref, defineAsyncComponent } from 'vue';

const DpplNestedDropdownChildren = defineAsyncComponent(() => import('@/components/datapeople/DpplNestedDropdown/DpplNestedDropdownChildren.vue'));

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  onClick: {
    type: Function,
    default: () => {},
  },
  children: {
    type: Array,
    default: () => [],
  },
  isTop: {
    type: Boolean,
    default: false,
  },
  hasDivider: {
    type: Boolean,
    default: false,
  },
  topLevelDirection: {
    type: String,
    default: 'bottom',
  },
  direction: {
    type: String,
    default: 'right',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  prependIcon: {
    type: Object,
    default: null,
  },
  prependIconClass: {
    type: String,
    default: '',
  },
  attributes: {
    type: Object,
    default: () => ({}),
  },
  href: {
    type: String,
    default: '',
  },
});

const emits = defineEmits(['topMouseEnter', 'update:modelValue']);

const hasChildren = computed(() => props.children.length > 0);

const open = ref(false);

const updateModelValue = (value) => {
  emits('update:modelValue', value);

  open.value = value;
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.nested-dropdown {
  background: @white;
  border-radius: 8px;
  .shadow();

  .item {
    padding: 0 16px;
    width: 100%;
    height: 44px;
    min-height: 44px;
    white-space: nowrap;
    word-wrap: normal;
    font-weight: 300;
    border-bottom: 1px solid @stroke-default;
    color: @text-darkest;

    &.has-divider {
      border-bottom: 1px solid @stroke-default;
    }

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: @bg-canvas;
      color: @text-brand;
    }
  }
}

.disabled {
  opacity: .3;
}
</style>

<style lang="less">
.dppl-nested-dropdown-content > .nested-dropdown {
  overflow: auto;
}
</style>
